import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useSpecialism } from "../contexts/SpecialismContext";
import { collection, getDocs, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { getAuth } from "firebase/auth";

const DeckCards: React.FC = () => {
  const { deck } = useParams<{ deck: string }>();
  const { specialismCode } = useSpecialism();
  const [cards, setCards] = useState<any[]>([]);
  const [debug, setDebug] = useState<string[]>([]);
  const [editing, setEditing] = useState<{ idx: number; field: "frontText" | "backText" } | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRefs = useRef<{ [key: number]: HTMLInputElement | null }>({});
  const fileInputRefsBack = useRef<{ [key: number]: HTMLInputElement | null }>({});
  const [showFullImage, setShowFullImage] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<{ idx: number; type?: "front" | "back" } | null>(null);
  // New states for creating card
  const [newFrontText, setNewFrontText] = useState("");
  const [newBackText, setNewBackText] = useState("");
  const [newFrontImage, setNewFrontImage] = useState<File | null>(null);
  const [newBackImage, setNewBackImage] = useState<File | null>(null);
  const [newFrontImagePreview, setNewFrontImagePreview] = useState<string | null>(null);
  const [newBackImagePreview, setNewBackImagePreview] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const newFrontTextRef = useRef<HTMLTextAreaElement>(null);
  const [contextMenu, setContextMenu] = useState<{ idx: number; x: number; y: number } | null>(null);  
  const [showDeleteCardModal, setShowDeleteCardModal] = useState<{ idx: number } | null>(null);
  const auth = getAuth();
  const currentUser = auth.currentUser;

  // Handle back image upload (replace)
  const handleBackImageUpload = async (file: File, idx: number) => {
    if (!specialismCode || !deck) return;
    const card = cards[idx];
    const colName = `flashcards_${specialismCode.toLowerCase()}`;
    const storage = getStorage();
    const basePath = `${colName}/${deck}`;
    const fullImgRef = ref(storage, `${basePath}/back_${Date.now()}_${file.name}`);
    try {
      // Delete previous images if present
      if (Array.isArray(card.backImageURL) && card.backImageURL.length > 0) {
        await deleteImagesFromStorage(card.backImageURL);
      }
      // Upload full image
      await uploadBytes(fullImgRef, file);
      const fullImgUrl = await getDownloadURL(fullImgRef);

      // Create low-res version
      const lowResBlob = await createLowResImage(file);
      const lowResName = `low-res_back_${Date.now()}_${file.name}`;
      const lowResRef = ref(storage, `${basePath}/${lowResName}`);
      await uploadBytes(lowResRef, lowResBlob);
      const lowResUrl = await getDownloadURL(lowResRef);

      // Save URLs to Firestore
      await updateDoc(doc(db, colName, card._docId), {
        backImageURL: [fullImgUrl, lowResUrl],
      });
      // Update local state
      const updatedCards = [...cards];
      updatedCards[idx].backImageURL = [fullImgUrl, lowResUrl];
      setCards(updatedCards);
      setDebug(prev => [...prev, `Uploaded back image for card ${card._docId}`]);
    } catch (err) {
      setDebug(prev => [...prev, `Back image upload error: ${err}`]);
    }
  };

  // Handle back image delete
  const handleDeleteBackImage = async (idx: number) => {
    if (!specialismCode) return;
    const card = cards[idx];
    const colName = `flashcards_${specialismCode.toLowerCase()}`;
    try {
      if (Array.isArray(card.backImageURL) && card.backImageURL.length > 0) {
        await deleteImagesFromStorage(card.backImageURL);
      }
      await updateDoc(doc(db, colName, card._docId), {
        backImageURL: [],
      });
      const updatedCards = [...cards];
      updatedCards[idx].backImageURL = [];
      setCards(updatedCards);
      setShowDeleteModal(null);
      setDebug(prev => [...prev, `Deleted back images for card ${card._docId}`]);
    } catch (err) {
      setDebug(prev => [...prev, `Delete back image error: ${err}`]);
    }
  };

  // Handle delete card
  const handleDeleteCard = async (idx: number) => {
    if (!specialismCode) return;
    const card = cards[idx];
    const colName = `flashcards_${specialismCode.toLowerCase()}`;
    try {
      // Delete images from storage if present
      if (Array.isArray(card.frontImageURL) && card.frontImageURL.length > 0) {
        await deleteImagesFromStorage(card.frontImageURL);
      }
      if (Array.isArray(card.backImageURL) && card.backImageURL.length > 0) {
        await deleteImagesFromStorage(card.backImageURL);
      }
      // Delete the Firestore document
      await deleteDoc(doc(db, colName, card._docId));
      // Remove from local state
      const updatedCards = [...cards];
      updatedCards.splice(idx, 1);
      setCards(updatedCards);
      setShowDeleteCardModal(null);
      setDebug(prev => [...prev, `Deleted card ${card._docId}`]);
    } catch (err) {
      setDebug(prev => [...prev, `Delete card error: ${err}`]);
    }
  };

  useEffect(() => {
    if (!specialismCode || !deck) {
      setDebug(["Missing specialismCode or deck"]);
      return;
    }
    const fetchCards = async () => {
      const colName = `flashcards_${specialismCode.toLowerCase()}`;
      setDebug([`Collection: ${colName}`]);
      try {
        const snapshot = await getDocs(collection(db, colName));
        setDebug(prev => [...prev, `Docs found: ${snapshot.size}`]);
        const deckCards: any[] = [];
        snapshot.forEach(docSnap => {
          const data = docSnap.data();
          if (data.deck === deck) {
            deckCards.push({ ...data, _docId: docSnap.id });
          }
        });
        // Sort by createdAt (descending: oldest first)
        deckCards.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return aTime - bTime;
        });
        setCards(deckCards);
        setDebug(prev => [...prev, `Cards in deck: ${deckCards.length}`]);
      } catch (err) {
        setDebug(prev => [...prev, `Error: ${err}`]);
      }
    };
    fetchCards();
  }, [specialismCode, deck]);

  // Focus input when editing
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  // ESC to close full image
  useEffect(() => {
    if (!showFullImage) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowFullImage(null);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showFullImage]);

  // Save edit to Firestore and update local state
  const saveEdit = async () => {
    if (!editing) return;
    const { idx, field } = editing;
    const card = cards[idx];
    const colName = `flashcards_${specialismCode?.toLowerCase()}`;
    try {
      await updateDoc(doc(db, colName, card._docId), { [field]: editValue });
      const updatedCards = [...cards];
      updatedCards[idx][field] = editValue;
      setCards(updatedCards);
      setEditing(null);
      setEditValue("");
    } catch (err) {
      setDebug(prev => [...prev, `Update error: ${err}`]);
    }
  };

  // Handle blur or Enter key
  const handleEditDone = async () => {
    await saveEdit();
  };

  // Delete images from Firebase Storage
  const deleteImagesFromStorage = async (urls: string[]) => {
    const storage = getStorage();
    for (const url of urls) {
      try {
        const path = new URL(url).pathname.split("/o/")[1];
        if (path) {
          const decodedPath = decodeURIComponent(path);
          await deleteObject(ref(storage, decodedPath));
        }
      } catch (err) {
        setDebug(prev => [...prev, `Delete image error: ${err}`]);
      }
    }
  };

  // Handle image upload (replace)
  const handleImageUpload = async (file: File, idx: number) => {
    if (!specialismCode || !deck) return;
    const card = cards[idx];
    const colName = `flashcards_${specialismCode.toLowerCase()}`;
    const storage = getStorage();
    const basePath = `${colName}/${deck}`;
    const fullImgRef = ref(storage, `${basePath}/${Date.now()}_${file.name}`);
    try {
      // Delete previous images if present
      if (Array.isArray(card.frontImageURL) && card.frontImageURL.length > 0) {
        await deleteImagesFromStorage(card.frontImageURL);
      }
      // Upload full image
      await uploadBytes(fullImgRef, file);
      const fullImgUrl = await getDownloadURL(fullImgRef);

      // Create low-res version
      const lowResBlob = await createLowResImage(file);
      const lowResName = `low-res_${Date.now()}_${file.name}`;
      const lowResRef = ref(storage, `${basePath}/${lowResName}`);
      await uploadBytes(lowResRef, lowResBlob);
      const lowResUrl = await getDownloadURL(lowResRef);

      // Save URLs to Firestore
      await updateDoc(doc(db, colName, card._docId), {
        frontImageURL: [fullImgUrl, lowResUrl],
      });
      // Update local state
      const updatedCards = [...cards];
      updatedCards[idx].frontImageURL = [fullImgUrl, lowResUrl];
      setCards(updatedCards);
      setDebug(prev => [...prev, `Uploaded image for card ${card._docId}`]);
    } catch (err) {
      setDebug(prev => [...prev, `Image upload error: ${err}`]);
    }
  };

  // Handle image delete
  const handleDeleteImage = async (idx: number) => {
    if (!specialismCode) return;
    const card = cards[idx];
    const colName = `flashcards_${specialismCode.toLowerCase()}`;
    try {
      if (Array.isArray(card.frontImageURL) && card.frontImageURL.length > 0) {
        await deleteImagesFromStorage(card.frontImageURL);
      }
      await updateDoc(doc(db, colName, card._docId), {
        frontImageURL: [],
      });
      const updatedCards = [...cards];
      updatedCards[idx].frontImageURL = [];
      setCards(updatedCards);
      setShowDeleteModal(null);
      setDebug(prev => [...prev, `Deleted images for card ${card._docId}`]);
    } catch (err) {
      setDebug(prev => [...prev, `Delete image error: ${err}`]);
    }
  };

  // Create a low-res version of the image (returns a Blob)
  const createLowResImage = (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      const reader = new FileReader();
      reader.onload = e => {
        img.src = e.target?.result as string;
      };
      img.onload = () => {
        // Resize to max 100px width, keep aspect ratio
        const maxWidth = 100;
        const scale = maxWidth / img.width;
        const canvas = document.createElement("canvas");
        canvas.width = maxWidth;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject("No canvas context");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          blob => {
            if (blob) resolve(blob);
            else reject("Failed to create blob");
          },
          "image/jpeg",
          0.6 // quality
        );
      };
      img.onerror = reject;
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Handlers for image selection
  const handleNewFrontImage = (file: File | null) => {
    setNewFrontImage(file);
    setNewFrontImagePreview(file ? URL.createObjectURL(file) : null);
  };
  const handleNewBackImage = (file: File | null) => {
    setNewBackImage(file);
    setNewBackImagePreview(file ? URL.createObjectURL(file) : null);
  };

  // Handler to create new card
  const handleCreateCard = async () => {
    if (!specialismCode || !deck) return;
    setCreating(true);
    const colName = `flashcards_${specialismCode.toLowerCase()}`;
    const storage = getStorage();
    const basePath = `${colName}/${deck}`;
    let frontImageURL: string[] = [];
    let backImageURL: string[] = [];

    try {
      // Upload front image if present
      if (newFrontImage) {
        const fullImgRef = ref(storage, `${basePath}/${Date.now()}_${newFrontImage.name}`);
        await uploadBytes(fullImgRef, newFrontImage);
        const fullImgUrl = await getDownloadURL(fullImgRef);
        const lowResBlob = await createLowResImage(newFrontImage);
        const lowResName = `low-res_${Date.now()}_${newFrontImage.name}`;
        const lowResRef = ref(storage, `${basePath}/${lowResName}`);
        await uploadBytes(lowResRef, lowResBlob);
        const lowResUrl = await getDownloadURL(lowResRef);
        frontImageURL = [fullImgUrl, lowResUrl];
      }
      // Upload back image if present
      if (newBackImage) {
        const fullImgRef = ref(storage, `${basePath}/back_${Date.now()}_${newBackImage.name}`);
        await uploadBytes(fullImgRef, newBackImage);
        const fullImgUrl = await getDownloadURL(fullImgRef);
        const lowResBlob = await createLowResImage(newBackImage);
        const lowResName = `low-res_back_${Date.now()}_${newBackImage.name}`;
        const lowResRef = ref(storage, `${basePath}/${lowResName}`);
        await uploadBytes(lowResRef, lowResBlob);
        const lowResUrl = await getDownloadURL(lowResRef);
        backImageURL = [fullImgUrl, lowResUrl];
      }
      // Add to Firestore
      const newDoc = {
        deck,
        frontText: newFrontText,
        backText: newBackText,
        frontImageURL,
        backImageURL,
        createdAt: { seconds: Math.floor(Date.now() / 1000) },
        createdBy: currentUser ? currentUser.uid : "unknown",
      };
      const docRef = await collection(db, colName);
      const { addDoc } = await import("firebase/firestore");
      const added = await addDoc(docRef, newDoc);
      setCards([...cards, { ...newDoc, _docId: added.id }]);
      // Reset fields
      setNewFrontText("");
      setNewBackText("");
      setNewFrontImage(null);
      setNewBackImage(null);
      setNewFrontImagePreview(null);
      setNewBackImagePreview(null);
      // Focus the new frontText textarea
      setTimeout(() => {
        newFrontTextRef.current?.focus();
      }, 0);
    } catch (err) {
      setDebug(prev => [...prev, `Create card error: ${err}`]);
    }
    setCreating(false);
  };

  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    window.addEventListener("click", close);
    window.addEventListener("scroll", close);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close);
    };
  }, [contextMenu]);

  return (
    <>
      <h2>Flashcards deck: {deck}</h2>
      
      {/* Big buttons for Read and Learn */}
      <div style={{ display: "flex", gap: "16px", marginBottom: "24px", justifyContent: "center" }}>
        <Link
          to={`/cards/${encodeURIComponent(deck!)}/read`}
          style={{
            background: "#1976d2",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "16px 32px",
            fontSize: "18px",
            fontWeight: "bold",
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 120,
            cursor: "pointer",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#1565c0")}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = "#1976d2")}
        >
          Read
        </Link>
        <Link
          to={`/cards/${encodeURIComponent(deck!)}/learn`}
          style={{
            background: "#388e3c",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "16px 32px",
            fontSize: "18px",
            fontWeight: "bold",
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 120,
            cursor: "pointer",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#2e7d32")}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = "#388e3c")}
        >
          Learn
        </Link>
      </div>

      <div style={{ paddingLeft: "5%", paddingRight: "5%" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "24px" }}>
          <thead>
            <tr style={{ background: "#f5f5f5" }}>
              <th style={{ border: "1px solid #ccc", padding: "8px" }}>Image</th>
              <th style={{ border: "1px solid #ccc", padding: "8px" }}>Front</th>
              <th style={{ border: "1px solid #ccc", padding: "8px" }}>Back</th>
              <th style={{ border: "1px solid #ccc", padding: "8px" }}>Image</th>
              {/* <th style={{ border: "1px solid #ccc", padding: "8px" }}>Created</th> */}
            </tr>
          </thead>
          <tbody>
            {cards.map((card, idx) => (
              <tr
                key={idx}
                onContextMenu={e => {
                  e.preventDefault();
                  setContextMenu({ idx, x: e.clientX, y: e.clientY });
                }}
              >
                <td
                  style={{
                    border: "1px solid #ccc",
                    padding: "8px",
                    textAlign: "center",
                    position: "relative", // <-- key for absolute positioning
                    minWidth: 90,
                  }}
                >
                  {card.frontImageURL && card.frontImageURL.length > 0 ? (
                    <>
                      <img
                        src={
                          Array.isArray(card.frontImageURL)
                            ? card.frontImageURL[1] || card.frontImageURL[0]
                            : card.frontImageURL
                        }
                        alt="Front"
                        style={{ maxWidth: 80, maxHeight: 80, cursor: "pointer", borderRadius: 4 }}
                        onClick={() =>
                          setShowFullImage(
                            Array.isArray(card.frontImageURL)
                              ? card.frontImageURL[0]
                              : card.frontImageURL
                          )
                        }
                      />
                      <div
                        className="img-icons"
                        style={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          display: "flex",
                          gap: 4,
                          opacity: 0,
                          transition: "opacity 0.2s",
                          zIndex: 2,
                        }}
                        onMouseEnter={e => (e.currentTarget.style.opacity = "1")}
                        onMouseLeave={e => (e.currentTarget.style.opacity = "0")}
                      >
                        {/* Edit icon */}
                        <button
                          style={{
                            background: "#fff",
                            border: "1px solid #1976d2",
                            borderRadius: "50%",
                            width: 28,
                            height: 28,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            marginRight: 2,
                          }}
                          title="Replace image"
                          onClick={e => {
                            e.stopPropagation();
                            fileInputRefs.current[idx]?.click();
                          }}
                        >
                          <span role="img" aria-label="edit" style={{ color: "#1976d2" }}>✏️</span>
                        </button>
                        {/* Delete icon */}
                        <button
                          style={{
                            background: "#fff",
                            border: "1px solid #e53935",
                            borderRadius: "50%",
                            width: 28,
                            height: 28,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                          title="Delete image"
                          onClick={e => {
                            e.stopPropagation();
                            setShowDeleteModal({ idx });
                          }}
                        >
                          <span role="img" aria-label="delete" style={{ color: "#e53935" }}>🗑️</span>
                        </button>
                      </div>
                      <input
                        type="file"
                        accept="image/jpeg,image/png"
                        style={{ display: "none" }}
                        ref={el => (fileInputRefs.current[idx] = el)}
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) handleImageUpload(file, idx);
                          // Reset input so same file can be selected again
                          if (fileInputRefs.current[idx]) fileInputRefs.current[idx]!.value = "";
                        }}
                      />
                      {/* Show icons on cell hover */}
                      <style>
                        {`
                          td:hover .img-icons { opacity: 1 !important; }
                        `}
                      </style>
                    </>
                  ) : (
                    <>
                      <button
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                          margin: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "100%",
                          height: 80,
                        }}
                        title="Upload image"
                        onClick={() => fileInputRefs.current[idx]?.click()}
                      >
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                          <path d="M12 16v-8M8 12h8" stroke="#888" strokeWidth="2" strokeLinecap="round" />
                          <rect x="3" y="3" width="18" height="18" rx="3" stroke="#888" strokeWidth="2" fill="none" />
                        </svg>
                      </button>
                      <input
                        type="file"
                        accept="image/jpeg,image/png"
                        style={{ display: "none" }}
                        ref={el => (fileInputRefs.current[idx] = el)}
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) handleImageUpload(file, idx);
                          // Reset input so same file can be selected again
                          if (fileInputRefs.current[idx]) fileInputRefs.current[idx]!.value = "";
                        }}
                      />
                    </>
                  )}
                </td>
                <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                  {editing?.idx === idx && editing.field === "frontText" ? (
                    <textarea
                      ref={inputRef}
                      value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      onBlur={handleEditDone}
                      onKeyDown={e => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleEditDone();
                        }
                      }}
                      style={{ width: "100%", boxSizing: "border-box", resize: "vertical" }}
                      rows={Math.max(2, editValue.split("\n").length)}
                    />
                  ) : (
                    <span
                      style={{ whiteSpace: "pre-line" }}
                      onClick={() => { setEditing({ idx, field: "frontText" }); setEditValue(card.frontText || ""); }}
                    >
                      {card.frontText || "-"}
                    </span>
                  )}
                </td>
                <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                  {editing?.idx === idx && editing.field === "backText" ? (
                    <textarea
                      ref={inputRef}
                      value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      onBlur={handleEditDone}
                      onKeyDown={e => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleEditDone();
                        }
                      }}
                      style={{ width: "100%", boxSizing: "border-box", resize: "vertical" }}
                      rows={Math.max(2, editValue.split("\n").length)}
                    />
                  ) : (
                    <span
                      style={{ whiteSpace: "pre-line" }}
                      onClick={() => { setEditing({ idx, field: "backText" }); setEditValue(card.backText || ""); }}
                    >
                      {card.backText || "-"}
                    </span>
                  )}
                </td>
                <td
                  style={{
                    border: "1px solid #ccc",
                    padding: "8px",
                    textAlign: "center",
                    position: "relative",
                    minWidth: 90,
                  }}
                >
                  {card.backImageURL && card.backImageURL.length > 0 ? (
                    <>
                      <img
                        src={
                          Array.isArray(card.backImageURL)
                            ? card.backImageURL[1] || card.backImageURL[0]
                            : card.backImageURL
                        }
                        alt="Back"
                        style={{ maxWidth: 80, maxHeight: 80, cursor: "pointer", borderRadius: 4 }}
                        onClick={() =>
                          setShowFullImage(
                            Array.isArray(card.backImageURL)
                              ? card.backImageURL[0]
                              : card.backImageURL
                          )
                        }
                      />
                      <div
                        className="img-icons"
                        style={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          display: "flex",
                          gap: 4,
                          opacity: 0,
                          transition: "opacity 0.2s",
                          zIndex: 2,
                        }}
                        onMouseEnter={e => (e.currentTarget.style.opacity = "1")}
                        onMouseLeave={e => (e.currentTarget.style.opacity = "0")}
                      >
                        {/* Edit icon */}
                        <button
                          style={{
                            background: "#fff",
                            border: "1px solid #1976d2",
                            borderRadius: "50%",
                            width: 28,
                            height: 28,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            marginRight: 2,
                          }}
                          title="Replace image"
                          onClick={e => {
                            e.stopPropagation();
                            fileInputRefsBack.current[idx]?.click();
                          }}
                        >
                          <span role="img" aria-label="edit" style={{ color: "#1976d2" }}>✏️</span>
                        </button>
                        {/* Delete icon */}
                        <button
                          style={{
                            background: "#fff",
                            border: "1px solid #e53935",
                            borderRadius: "50%",
                            width: 28,
                            height: 28,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                          title="Delete image"
                          onClick={e => {
                            e.stopPropagation();
                            setShowDeleteModal({ idx, type: "back" });
                          }}
                        >
                          <span role="img" aria-label="delete" style={{ color: "#e53935" }}>🗑️</span>
                        </button>
                      </div>
                      <input
                        type="file"
                        accept="image/jpeg,image/png"
                        style={{ display: "none" }}
                        ref={el => (fileInputRefsBack.current[idx] = el)}
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) handleBackImageUpload(file, idx);
                          if (fileInputRefsBack.current[idx]) fileInputRefsBack.current[idx]!.value = "";
                        }}
                      />
                      <style>
                        {`
                          td:hover .img-icons { opacity: 1 !important; }
                        `}
                      </style>
                    </>
                  ) : (
                    <>
                      <button
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                          margin: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "100%",
                          height: 80,
                        }}
                        title="Upload image"
                        onClick={() => fileInputRefsBack.current[idx]?.click()}
                      >
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                          <path d="M12 16v-8M8 12h8" stroke="#888" strokeWidth="2" strokeLinecap="round" />
                          <rect x="3" y="3" width="18" height="18" rx="3" stroke="#888" strokeWidth="2" fill="none" />
                        </svg>
                      </button>
                      <input
                        type="file"
                        accept="image/jpeg,image/png"
                        style={{ display: "none" }}
                        ref={el => (fileInputRefsBack.current[idx] = el)}
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) handleBackImageUpload(file, idx);
                          if (fileInputRefsBack.current[idx]) fileInputRefsBack.current[idx]!.value = "";
                        }}
                      />
                    </>
                  )}
                </td>
                {/* Remove or comment out the Created column cell */}
                {/* <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                  {(card.createdAt ? new Date(card.createdAt.seconds * 1000).toLocaleDateString() : "-") +
                    (card.createdBy ? ` by ${card.createdBy}` : "")}
                </td> */}
              </tr>
            ))}
            {/* New row for creating card */}
            <tr>
              {/* Front image */}
              <td style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                {newFrontImagePreview ? (
                  <img src={newFrontImagePreview} alt="Preview" style={{ maxWidth: 80, maxHeight: 80, borderRadius: 4 }} />
                ) : (
                  <>
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      style={{ display: "none" }}
                      id="new-front-image"
                      onChange={e => handleNewFrontImage(e.target.files?.[0] || null)}
                    />
                    <label htmlFor="new-front-image" style={{ cursor: "pointer" }}>
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                        <path d="M12 16v-8M8 12h8" stroke="#888" strokeWidth="2" strokeLinecap="round" />
                        <rect x="3" y="3" width="18" height="18" rx="3" stroke="#888" strokeWidth="2" fill="none" />
                      </svg>
                    </label>
                  </>
                )}
              </td>
              {/* Front text */}
              <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                <textarea
                  ref={newFrontTextRef}
                  value={newFrontText}
                  onChange={e => setNewFrontText(e.target.value)}
                  placeholder="Front text"
                  style={{ width: "100%", boxSizing: "border-box", resize: "vertical" }}
                  rows={2}
                  onKeyDown={e => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleCreateCard();
                    }
                  }}
                />
              </td>
              {/* Back text */}
              <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                <textarea
                  value={newBackText}
                  onChange={e => setNewBackText(e.target.value)}
                  placeholder="Back text"
                  style={{ width: "100%", boxSizing: "border-box", resize: "vertical" }}
                  rows={2}
                  onKeyDown={e => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleCreateCard();
                    }
                  }}
                />
              </td>
              {/* Back image */}
              <td style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                {newBackImagePreview ? (
                  <img src={newBackImagePreview} alt="Preview" style={{ maxWidth: 80, maxHeight: 80, borderRadius: 4 }} />
                ) : (
                  <>
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      style={{ display: "none" }}
                      id="new-back-image"
                      onChange={e => handleNewBackImage(e.target.files?.[0] || null)}
                    />
                    <label htmlFor="new-back-image" style={{ cursor: "pointer" }}>
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                        <path d="M12 16v-8M8 12h8" stroke="#888" strokeWidth="2" strokeLinecap="round" />
                        <rect x="3" y="3" width="18" height="18" rx="3" stroke="#888" strokeWidth="2" fill="none" />
                      </svg>
                    </label>
                  </>
                )}
              </td>
              {/* Remove or comment out the Created column cell */}
              {/* <td style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                <button
                  onClick={handleCreateCard}
                  disabled={creating || (!newFrontText && !newBackText && !newFrontImage && !newBackImage)}
                  style={{
                    background: "#1976d2",
                    color: "#fff",
                    border: "none",
                    borderRadius: 4,
                    padding: "8px 24px",
                    cursor: creating ? "not-allowed" : "pointer",
                    fontWeight: "bold",
                  }}
                >
                  {creating ? "Creating..." : "Add"}
                </button>
              </td> */}
            </tr>
          </tbody>
        </table>
      </div>
      {/* Full image modal */}
      {showFullImage && (
        <div
          style={{
            position: "fixed",
            zIndex: 1000,
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setShowFullImage(null)}
        >
          <img
            src={showFullImage}
            alt="Full"
            style={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              boxShadow: "0 0 24px #000",
              background: "#fff",
              borderRadius: 8,
            }}
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
      {/* Delete image confirmation modal */}
      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            zIndex: 1100,
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setShowDeleteModal(null)}
        >
          <div
            style={{
              background: "#fff",
              padding: "32px",
              borderRadius: 8,
              boxShadow: "0 0 24px #000",
              minWidth: 320,
              textAlign: "center",
            }}
            onClick={e => e.stopPropagation()}
          >
            <p style={{ marginBottom: 24 }}>Are you sure you want to delete this image?</p>
            <button
              style={{
                background: "#e53935",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                padding: "8px 24px",
                marginRight: 16,
                cursor: "pointer",
                fontWeight: "bold",
              }}
              onClick={() => {
                if (showDeleteModal.type === "back") handleDeleteBackImage(showDeleteModal.idx);
                else handleDeleteImage(showDeleteModal.idx);
              }}
            >
              Delete
            </button>
            <button
              style={{
                background: "#1976d2",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                padding: "8px 24px",
                cursor: "pointer",
                fontWeight: "bold",
              }}
              onClick={() => setShowDeleteModal(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {/* Delete card confirmation modal */}
      {showDeleteCardModal && (
        <div
          style={{
            position: "fixed",
            zIndex: 1100,
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setShowDeleteCardModal(null)}
        >
          <div
            style={{
              background: "#fff",
              padding: "32px",
              borderRadius: 8,
              boxShadow: "0 0 24px #000",
              minWidth: 320,
              textAlign: "center",
            }}
            onClick={e => e.stopPropagation()}
          >
            <p style={{ marginBottom: 24 }}>
              Are you sure you want to delete this flashcard? This will remove the card and its images.
            </p>
            <button
              style={{
                background: "#e53935",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                padding: "8px 24px",
                marginRight: 16,
                cursor: "pointer",
                fontWeight: "bold",
              }}
              onClick={() => handleDeleteCard(showDeleteCardModal.idx)}
            >
              Delete
            </button>
            <button
              style={{
                background: "#1976d2",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                padding: "8px 24px",
                cursor: "pointer",
                fontWeight: "bold",
              }}
              onClick={() => setShowDeleteCardModal(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {contextMenu && (
        <div
          style={{
            position: "fixed",
            top: contextMenu.y,
            left: contextMenu.x,
            background: "#fff",
            border: "1px solid #ccc",
            borderRadius: 4,
            zIndex: 2000,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            minWidth: 140,
          }}
          onClick={() => setContextMenu(null)}
          onContextMenu={e => e.preventDefault()}
        >
          <div
            style={{
              padding: "10px 18px",
              cursor: "pointer",
              color: "#e53935",
              fontWeight: 500,
              userSelect: "none",
            }}
            onClick={() => {
              setShowDeleteCardModal({ idx: contextMenu.idx });
              setContextMenu(null);
            }}
          >
            Delete this flashcard
          </div>
        </div>
      )}
      {/*<h4>Debug Output:</h4> */}
      {/*<pre style={{ background: "#f5f5f5", padding: "8px", fontSize: "0.9em" }}>
        {debug.join("\n")}
      </pre> */}
    </>
  );
};

export default DeckCards;